import { useState, useEffect } from 'react';
import chain from 'ramda/es/chain';
import compose from 'ramda/es/compose';
import filter from 'ramda/es/filter';
import flatten from 'ramda/es/flatten';
import groupBy from 'ramda/es/groupBy';
import map from 'ramda/es/map';
import prop from 'ramda/es/prop';
import sort from 'ramda/es/sort';
import split from 'ramda/es/split';
import uniq from 'ramda/es/uniq';
import uniqBy from 'ramda/es/uniqBy';
import { stringify } from 'query-string';

import { salesforceGroup } from '@src/scripts/shared/constants';
import { getLangCode } from '@src/scripts/utils';
import l10n from './salesForce.l10n';

export const isDev = process.env.NODE_ENV === 'development';
export const SALEFORCE_API_BASE = isDev
	? 'https://hs-fresenius.my.salesforce-sites.com/services/apexrest'
	: 'https://hs-fresenius.my.salesforce-sites.com/services/apexrest';

const fields = [
	'standort',
	'studienbeginn',
	'regelstudienzeit',
	'ects',
	'preis',
];
const detailFields = [
	'studienbeginn',
	'regelstudienzeit',
	'sprache',
	'studiengangsform',
	'akkreditierung',
	'preis',
	'anmeldegebuehr',
	'bewerbungsfrist',
	'abschluss',
];

const labels = l10n.labels;
const suffix = l10n.suffix;

const icons = {
	bewerbungsfrist: '',
	regelstudienzeit: 'Regelstudienzeit',
	studienbeginn: 'ZeitAlternative2',
	abschluss: '',
	sprache: '',
	ects: 'Visum',
	preis: 'SemestergebuehrenAlternative1',
	akkreditierung: '',
	standort: 'Ort',
};

/*map( ( p ) => ( {
	f,
	v:
		( labels[ f ] ? labels[ f ] + ': ' : '' ) +
		( p[ f ] || '' ).trim() +
		( suffix[ f ] ? suffix[ f ] : '' ),
	i: icons[ f ] || icons.studiengangsform,
} ) )*/

const getFinishObject = ( item, isDetail ) => {
	const f = item.f;
	const isPriceDetail = isDetail && f === 'preis';
	return {
		...item,
		v:
			( item.v || '' ).trim() +
			( suffix[ f ] && ! isPriceDetail ? suffix[ f ] : '' ),
	};
};

const getSingleVals = () => ( x ) => {
	let hasStar = false;
	return Object.values( x ).map( ( p ) => {
		switch ( p[ 0 ].f ) {
			case 'regelstudienzeit':
				const a1 = compose(
					flatten(),
					uniq(),
					map( ( a ) => a.label ),
					filter( ( a ) => a.value ),
					sort( ( a, b ) => ( a.value > b.value ? 1 : -1 ) ),
					uniq(),
					map( ( a ) => ( {
						label: a,
						value: parseInt( a ),
					} ) ),
					flatten(),
					map( ( pi ) => pi.v.split( ' • ' ) )
				)( p );
				return {
					...p[ 0 ],
					v: `${ a1[ 0 ] }${
						a1.length > 1 ? ' - ' + a1[ a1.length - 1 ] : ''
					}`,
				};
			case 'preis':
				hasStar = false;
				const nums = compose(
					filter( ( n ) => n ),
					map( ( pi ) => {
						if ( pi.v.includes( '*' ) ) {
							hasStar = true;
						}
						return parseFloat( pi.v.replace( '.', '' ) );
					} )
				)( p );
				const min = Math.min.apply( Math, nums );
				return {
					...p[ 0 ],
					v:
						p.length === 1
							? p[ 0 ].v
							: `ab ${ min.toFixed( 2 ).replace( '.', ',' ) } €${
									hasStar ? '*' : ''
							  }`,
				};
			case 'ects':
				hasStar = false;
				const nums1 = compose(
					filter( ( pi ) => pi > 0 ),
					map( ( pi ) => {
						if ( pi.v.includes( '*' ) ) {
							hasStar = true;
						}

						return pi.v ? parseFloat( pi.v ) : 0;
					} )
				)( p );
				const min1 = Math.min.apply( Math, nums1 );
				return {
					...p[ 0 ],
					v:
						p.length === 1
							? p[ 0 ].v
							: 'ab ' + min1 + ( hasStar ? '*' : '' ),
				};
			case 'standort':
				return {
					...p[ 0 ],
					v: p.map( ( pi ) => pi.v ).join( ', ' ),
				};
			case 'akkreditierung':
				return p[ p.length - 1 ];
			default:
				return p[ 0 ];
		}
	} );
};

const mostFrequent = ( array ) => {
	let maxcount = 0;
	let mfitem;
	for ( let i = 0; i < array.length; i++ ) {
		let count = 0;
		for ( let j = 0; j < array.length; j++ ) {
			if ( array[ i ] === array[ j ] ) count++;
		}

		if ( count > maxcount ) {
			maxcount = count;
			mfitem = array[ i ];
		}
	}

	return mfitem;
};

const getFinalFields = ( produkte, _fields = fields, isDetail = false ) => {
	if ( ! produkte.length ) {
		return [];
	}
	return compose(
		map( ( p ) => getFinishObject( p, isDetail ) ),
		getSingleVals(),
		groupBy( prop( 'f' ) ),
		chain( ( f ) =>
			compose(
				uniqBy( prop( 'v' ) ),
				( value ) => {
					if ( value[ 0 ].f === 'akkreditierung' ) {
						const akkreditierung = [];
						akkreditierung[ 0 ] = mostFrequent( value );
						value = akkreditierung;
					}

					return value;
				},
				formatKeyItem( f, isDetail )
			)( produkte )
		)
	)( _fields );
};

const getStartVal = ( x ) =>
	compose(
		( p ) => {
			if (
				x.length > 0 &&
				( 'Fernstudium' === x[ 0 ].standort ||
					'Distance learning' === x[ 0 ].standort ||
					! p.length )
			) {
				return x[ 0 ].studienbeginn;
			}
			switch ( true ) {
				case p.includes( 'WS' ) && ! p.includes( 'SS' ):
					return l10n.wintersemester;
				case ! p.includes( 'WS' ) && p.includes( 'SS' ):
					return l10n.sommersemester;
				default:
					return l10n.winter_sommersemester;
			}
		},
		uniq(),
		flatten(),
		map( ( p ) => {
			const hasWinter = p.studienbeginn_semester.includes( 'WS' );
			const hasSummer = p.studienbeginn_semester.includes( 'SS' );
			return [
				...( hasWinter ? [ 'WS' ] : [] ),
				...( hasSummer ? [ 'SS' ] : [] ),
			];
		} )
	)( x );

const formatKeyItem =
	( f, isDetail = false ) =>
	( x ) => {
		if ( f === 'studienbeginn' ) {
			const value = getStartVal( x );
			return [
				{
					f,
					l: labels[ f ],
					v: value.trim(),
					i: icons[ f ] || icons.studiengangsform,
				},
			];
		}
		if ( isDetail && f === 'preis' ) {
			const priceDetail = compose( groupBy( prop( 'preis' ) ) )( x );
			if ( Object.keys( priceDetail ).length === 1 ) {
					console.log(x[0]) //eslint-disable-line
				const nonEu = x[ 0 ].preis_non_eu
					? ` + ${ x[ 0 ].preis_non_eu } ${ l10n.per_month_non_eu }`
					: '';
				return [
					{
						f: `${ f }`,
						l: `${ labels[ f ] }`,
						v: `${ x[ 0 ].preis } ${ suffix[ f ] || '' }${
							isDetail ? nonEu : ''
						}`,
						i: icons[ f ] || icons.studiengangsform,
					},
				];
			}
			const value = Object.keys( priceDetail )
				.sort( ( a, b ) => ( a > b ? 1 : -1 ) )
				.map( ( p ) => {
					const locations = priceDetail[ p ].map(
						( i ) => i.standort
					);
					const uniqLocations = [ ...new Set( locations ) ];
					return `<b>${ uniqLocations.join( ', ' ) }</b><br/>${ (
						p || ''
					).trim() } ${ suffix[ f ] || '' }`;
				} );

			return [
				{
					f: `${ f }`,
					l: `${ labels[ f ] }`,
					v: value.join( '<br /><br />' ),
					i: icons[ f ] || icons.studiengangsform,
				},
			];
		}

		return x.map( ( p ) => {
			return {
				f,
				l: labels[ f ],
				v: ( p[ f ] || '' ).trim(),
				i: icons[ f ] || icons.studiengangsform,
			};
		} );
	};

const getBeratungFields = ( produkte ) =>
	compose(
		uniqBy( prop( 'standort' ) ),
		map( ( p ) => {
			return {
				standort: p.standort,
				name: p.studienberater,
				mail: p.studienberater_email,
				foto: p.studienberater_foto,
				telefon: p.studienberater_telefon,
			};
		} )
	)( produkte );

const mkGetFilterOptions =
	( products ) =>
	( labelSlug, labelEqualsValue = false ) => {
		const valueSlug = labelEqualsValue ? labelSlug : `${ labelSlug }_kurz`;
		return compose(
			map( ( { [ labelSlug ]: label, [ valueSlug ]: value } ) => ( {
				label,
				value,
			} ) ),
			filter( compose( ( value ) => !! value, prop( valueSlug ) ) ),
			uniqBy( prop( valueSlug ) )
		)( products );
	};

const mkGetFilterOptionsMultipleValuesPerField = ( products ) => ( slug ) => {
	return compose(
		map( ( value ) => ( { label: value, value } ) ),
		filter( ( value ) => !! value ),
		uniq,
		chain( compose( split( ';' ), prop( slug ) ) )
	)( products );
};

export const mkSfApiUrl = ( endpoint, queryParams = {} ) => {
	const qs = stringify(
		{ gruppe: salesforceGroup, lng: getLangCode(), ...queryParams },
		{ arrayFormat: 'comma' }
	);
	return `${ SALEFORCE_API_BASE }/${ endpoint }${ qs ? `?${ qs }` : '' }`;
};

export const getSalesForce = async ( queryParams = {} ) => {
	const response = await fetch( mkSfApiUrl( 'WebsiteProdukt', queryParams ) );
	const data = await response.json();
	return {
		data: data
			? filter(
					( { sprache } ) =>
						getLangCode() === 'en' ? sprache === 'English' : true,
					data
			  )
			: [],
	};
};

/**
 * @param {string}                   needle
 * @param {string | Array | boolean} haystack
 */
export const compareSalesforceIds = ( needle = '', haystack ) => {
	switch ( typeof haystack ) {
		case typeof []:
			return haystack.includes( needle ) || ! haystack.length;
		case typeof '':
			return haystack === needle;
		default:
			return true;
	}
};

const salesforceCache = {};

export const useSalesForce = ( {
	websiteCode = '',
	abschlussartKurz = '',
	studiengangsformKurz = '',
	standorteKurz = '',
	paidLandingpage = '',
	salesforceId = false,
} = {} ) => {
	const [ produkte, updateProdukte ] = useState( [] );
	const [ loading, updateLoading ] = useState( true );

	useEffect( () => {
		const controller = new AbortController();

		const url = `${ SALEFORCE_API_BASE }/WebsiteProdukt?gruppe=${ salesforceGroup }&lng=${ getLangCode() }${
			abschlussartKurz ? '&abschlussart=' + abschlussartKurz : ''
		}${
			studiengangsformKurz
				? '&studiengangsform=' + studiengangsformKurz
				: ''
		}`;

		if ( salesforceCache[ url ] ) {
			updateProdukte( salesforceCache[ url ] );
			updateLoading( false );
		} else {
			fetch( url, { signal: controller.signal } )
				.then( ( r ) => r.json() )
				.then( ( ps ) => {
					const ps_ = websiteCode
						? ps.filter(
								( item ) => websiteCode === item.website_code
						  )
						: ps;

					const pByLocation = standorteKurz
						? ps_.filter( ( item ) =>
								standorteKurz
									.split( ';' )
									.filter( Boolean )
									.includes( item.standort_kurz )
						  )
						: ps_;

					updateProdukte( pByLocation );
					updateLoading( false );
					salesforceCache[ url ] = pByLocation;
				} )
				.catch( ( error ) => {
					if ( error.name === 'AbortError' ) {
						console.log('Fetch aborted'); // eslint-disable-line
					} else {
						console.warn(error) // eslint-disable-line
					}
				} );
		}
		return () => {
			// cancel the request before component unmounts
			controller.abort();
		};
	}, [] );

	const filteredProdukte = produkte.filter( ( x ) => {
		const filterID = compareSalesforceIds( x.salesforceId, salesforceId );

		const filterPaid = paidLandingpage
			? makeCodeTest( paidLandingpage || '' )( x.code )
			: true;

		return filterID && filterPaid;
	} );

	const getFilterOptions = mkGetFilterOptions( produkte );
	const getFilterOptionsMultipleValuesPerField =
		mkGetFilterOptionsMultipleValuesPerField( produkte );

	const allgHinweis = compose(
		uniq(),
		map( ( p ) => {
			const arr = [];
			arr.push( p.regelstudienzeit_hinweis );
			arr.push( p.allg_hinweis );
			return arr;
		} )
	)( filteredProdukte );

	return {
		produkte: filteredProdukte,
		facts: getFinalFields( filteredProdukte ),
		detailFacts: getFinalFields( filteredProdukte, detailFields, true ),
		beratung: getBeratungFields( filteredProdukte ),
		allgHinweis,
		loading,
		filterOptions: {
			abschlussart: getFilterOptions( 'abschlussart' ),
			fachbereich: getFilterOptions( 'fachbereich' ),
			sprache: getFilterOptions( 'sprache', true ),
			standort: getFilterOptions( 'standort' ),
			studienbeginn_semester: getFilterOptionsMultipleValuesPerField(
				'studienbeginn_semester'
			),
			// since the studiengangsform short forms BB, BBfs & BBpbl all have the same long form "Berufsbegleitend"
			// we use the value as the label in Gutenberg
			studiengangsform: getFilterOptions( 'studiengangsform_kurz', true ),
			themenschwerpunkt: getFilterOptionsMultipleValuesPerField(
				'themenschwerpunkt_kurz'
			),
		},
	};
};

const ebene3Regex = /^([^_]+)_([^_]+)_([^_]+)_([^_]+)$/i;
const ebene2Regex = /^([^_]+)_([^_]+)_([^_]+)$/i;

// takes an ebene 2 or ebene 3 product code and returns a function that
// tests an ebene 3 product code for a match
export const makeCodeTest = ( code ) => {
	const matches = code.match( ebene2Regex );
	if ( matches ) {
		return ( str ) =>
			new RegExp(
				`^${ matches[ 1 ] }_${ matches[ 2 ] }_[^_]+_${ matches[ 3 ] }$`,
				'i'
			).test( str );
	}
	if ( ebene3Regex.test( code ) ) {
		return ( str ) => new RegExp( `^${ code }$`, 'i' ).test( str );
	}
	return () => false;
};

export const isEbene2Code = ( code ) => ebene2Regex.test( code );

export const isEbene3Code = ( code ) => ebene3Regex.test( code );
